import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, Sparkles, ShieldCheck, Loader2 } from 'lucide-react';
import { processAIQuery } from '../services/aiAgentService';

export default function PrivateAIAgent({ context }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: 'שלום! אני סוכן ה-AI החכם של המשרד (מופעל ע"י גוגל Gemini). איך אוכל לעזור לך? (למשל: נסח מסמך התראה לתיק כהן נגד לוי)', sender: 'ai' }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { id: Date.now(), text: input.trim(), sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    
    try {
      const response = await processAIQuery(userMessage.text, context);
      const aiMessage = { id: Date.now() + 1, text: response.text, sender: 'ai' };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: 'הייתה בעיה בתקשורת מול ה-AI.', sender: 'ai' }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 left-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-2xl shadow-blue-900/50 flex items-center justify-center text-white hover:scale-110 active:scale-95 transition-all z-50 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        title="סוכן AI חכם"
      >
        <Sparkles className="w-6 h-6 animate-pulse" />
      </button>

      {/* Chat Dialog */}
      <div 
        className={`fixed bottom-6 left-6 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 flex flex-col transition-all duration-300 transform origin-bottom-left ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-0 opacity-0 translate-y-10 pointer-events-none'}`}
        style={{ height: '500px', maxHeight: 'calc(100vh - 48px)' }}
        dir="rtl"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-blue-950 p-4 rounded-t-2xl flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-400/30">
              <Bot className="w-6 h-6 text-blue-300" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">סוכן AI חכם</h3>
              <p className="text-blue-200 text-[10px] flex items-center gap-1 mt-0.5">
                <ShieldCheck className="w-3 h-3" /> מבוסס מודל Gemini 2.5
              </p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-start' : 'justify-end'}`}>
              <div 
                className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm whitespace-pre-wrap ${
                  msg.sender === 'user' 
                    ? 'bg-blue-600 text-white rounded-tr-none' 
                    : 'bg-white border border-slate-200 text-slate-700 rounded-tl-none'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="max-w-[85%] p-3 rounded-2xl text-sm shadow-sm bg-white border border-slate-200 text-slate-700 rounded-tl-none flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                קורא ומנתח מסמכים...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 bg-white border-t border-slate-200 rounded-b-2xl shrink-0">
          <div className="flex items-end gap-2 bg-slate-100 rounded-xl p-1 border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
            <textarea
              className="flex-1 bg-transparent border-none focus:ring-0 resize-none max-h-32 text-sm p-2 text-slate-700 placeholder-slate-400 outline-none"
              placeholder="שאל אותי משהו על הנתונים במערכת..."
              rows={1}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onKeyDown={handleKeyDown}
            />
            <button 
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:hover:bg-blue-600 shrink-0"
          >
            {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
          </div>
          <div className="text-center mt-2">
            <span className="text-[9px] text-slate-400">מופעל באמצעות בינה מלאכותית יוצרת (Gemini 2.5 Flash)</span>
          </div>
        </div>
      </div>
    </>
  );
}
